import { get } from "lib/http";
import { Cart } from "types/cart";
import { Catalog, RefurbishedProduct } from "types/refurbishedProduct";

const CATALOGS_PATH = "/ecom/catalogs/";
const LOCAL_CATALOGS_STORAGE_KEY = "clientCatalogs";
const NATIONAL_CATALOGS_STORAGE_KEY = "clientNationalCatalogs";
const ACTIVATION_STORAGE_KEY = "clientFilterActivation";
const DEFAULT_CATALOG_STORAGE_KEY = "defaultCatalog";

const NATIONAL_CATALOG_NAME = "national";

/**
 * stores in local storage the default catalog to show to the client
 * @param catalogSlug slug of the default catalog to store
 * @returns nothing
 */
export const setDefaultCatalog = (catalogSlug: string) => {
  if (typeof window == "undefined") return;
  window.localStorage.setItem(DEFAULT_CATALOG_STORAGE_KEY, JSON.stringify(catalogSlug));
};

/**
 * @returns the list of catalog slugs available for the client
 */
const getClientDefaultCatalogList = (): string[] => {
  if (typeof window == "undefined") return [NATIONAL_CATALOG_NAME];
  const defaultCatalogFromLocalStorage = JSON.parse(window.localStorage.getItem(DEFAULT_CATALOG_STORAGE_KEY) as string);
  return defaultCatalogFromLocalStorage ? [defaultCatalogFromLocalStorage] : [NATIONAL_CATALOG_NAME];
};

/**
 * @param local specifies if the api should return local catalogs or not
 * @param zipcode client postal code
 * @returns the list of Murfy Database catalogs available for the client based on local and zipcode filters
 */
const getCatalogsByZipcode = async (local: boolean, zipcode: string): Promise<Catalog[]> => {
  if (typeof window == "undefined") return [];
  const data = await get(`${CATALOGS_PATH}?local=${local}&zipcode=${zipcode}`);
  return data;
};

/**
 * sets filter activation and the catalogs available for client in local storage
 * @param zipcode client postal code
 * @param activate local offer activation
 * @returns the catalogs available for the client (local and national)
 */
export const setEligibility = async (zipcode: string, activate = false) => {
  if (typeof window == "undefined") return;
  window.localStorage.setItem(ACTIVATION_STORAGE_KEY, JSON.stringify(activate));
  const localCatalogs = await getCatalogsByZipcode(true, zipcode);
  window.localStorage.setItem(LOCAL_CATALOGS_STORAGE_KEY, JSON.stringify(localCatalogs));
  const nationalCatalogs = await getCatalogsByZipcode(false, zipcode);
  window.localStorage.setItem(NATIONAL_CATALOGS_STORAGE_KEY, JSON.stringify(nationalCatalogs));
  return [...localCatalogs, ...nationalCatalogs];
};

/**
 * @returns the list of local catalogs available for the client
 */
export const getClientLocalCatalogs = (): string[] => {
  if (typeof window == "undefined") return [];
  const catalogsFromLocalStorage = JSON.parse(window.localStorage.getItem(LOCAL_CATALOGS_STORAGE_KEY) as string);
  return catalogsFromLocalStorage?.map((catalog) => catalog.slug) || [];
};

/**
 * @returns the list of national catalogs available for the client
 */
export const getClientNationalCatalogs = (): string[] => {
  if (typeof window == "undefined") return getClientDefaultCatalogList();
  const catalogsFromLocalStorage = JSON.parse(window.localStorage.getItem(NATIONAL_CATALOGS_STORAGE_KEY) as string);
  return catalogsFromLocalStorage?.map((catalog) => catalog.slug) || getClientDefaultCatalogList();
};

/**
 * @returns the status of the local filter
 */
export const getFilterActivation = () => {
  if (typeof window == "undefined") return undefined;
  return JSON.parse(window.localStorage.getItem(ACTIVATION_STORAGE_KEY) as string);
};

/**
 * removes the local catalogs from local storage and resets the activation of the local filter
 * @returns nothing
 */
export const resetEligibility = () => {
  if (typeof window == "undefined") return;
  window.localStorage.setItem(LOCAL_CATALOGS_STORAGE_KEY, JSON.stringify([]));
  window.localStorage.setItem(ACTIVATION_STORAGE_KEY, JSON.stringify(false));
};

/**
 * checks product eligibility to local offer for the client
 * @param product product shown to the client
 * @returns true if product's catalog is in client's local catalog list else false
 */
export const getProductEligibility = (product) => {
  // Return true if product catalog is in client catalog else false
  const clientCatalogs = getClientLocalCatalogs();
  const productCatalogs: string[] = [];
  product.available_appearances?.map((appearance) =>
    appearance.catalogs?.map((catalog) => productCatalogs.push(catalog.slug))
  );
  product.catalogs?.map((catalog) => productCatalogs.push(catalog.slug));
  return productCatalogs.some((catalog) => clientCatalogs?.includes(catalog));
};

/**
 *
 * @param cart client's list of products
 * @returns true if all products are available for the client
 */
export const getCartEligibility = (cart: Cart): boolean => {
  const isClientEligibleToLocalOffer = cart.selection
    .map((selection) => getProductEligibility(selection.product))
    .every((eligibility) => eligibility);
  return isClientEligibleToLocalOffer;
};

/**
 *
 * @param cart client's list of products
 * @returns list of products that are not available for the client
 */
export const getListOfProductsNotAvailableForClient = (cart: Cart): RefurbishedProduct[] => {
  const localCatalogs = getClientLocalCatalogs();
  const nationalCatalogs = getClientNationalCatalogs();
  const clientCatalogs = [...nationalCatalogs, ...localCatalogs];

  // a product is not eligible if none of its catalogs is available for the client
  const notEligibleCartSelection = cart.selection.filter(
    (item) => !item.product.catalogs.some((catalog) => clientCatalogs.includes(catalog.slug))
  );
  return notEligibleCartSelection.map((selection) => selection.product);
};

/**
 * checks if the product is available with the local offer for the client and if not
 * checks if it is available without the offer
 * @param products list of products shown to the client
 * @returns products available for the client in the given list
 */
export const getAvailableProductsFromList = (products) => {
  return products.filter(
    (product) =>
      getProductEligibility({ available_appearances: [product] }) ||
      product.catalogs.some((catalog) => getClientNationalCatalogs().includes(catalog.slug))
  );
};

/**
 * @param local whether the local catalogs shoulb be shown or not
 * @param defaultCatalogSlug slug of a catalog that has to be shown to the client
 * @returns the list of catalogs the client can see
 */
export const getClientCatalogs = (local: boolean, defaultCatalogSlug?: string): string[] => {
  const localCatalogsNames = (local && getClientLocalCatalogs()) || [];
  return localCatalogsNames.length > 0
    ? localCatalogsNames
    : defaultCatalogSlug
    ? [defaultCatalogSlug]
    : getClientNationalCatalogs();
};
